import { NSARouterMakerResult } from "namirasoft-account-react";
import { IStorageLocal, NamingConvention } from "namirasoft-core";
import { AgentTrackResponse_AnalyzerRow, AgentTrackResponse_ClassificationRow, AgentTrackResponse_ErrorRow, AgentTrackResponse_ObservationRow, AgentTrackResponse_PlanRow, AgentTrackResponse_ReportPlanRow, AgentTrackResponse_StepRow, AgentTrackResponse_TaskRow, AgentTrackResponse_ToolRow, AWorkflowStateType_ResourceRequestedByType, ChatRow, RunHelper, RunRow } from "namirasoft-expert";
import { NamirasoftMap } from "namirasoft-site-map";
import { JsonFormatter, NSID, NSLoading, NSMarkdown } from "namirasoft-site-react";
import { Component, MouseEvent, ReactNode } from "react";
import { ExpertRouterProps } from "../ExpertRouterProps";
import Styles from "./RunAgent.module.css";
import RunLayout from "./RunLayout";
import { StatusBadge, StatusCount, StatusKind } from "./StatusBadge";
import Task from "./Task";

const THINKING_WORDS: string[] = [
    "Mombeling",
    "Spicying",
    "Cooking",
    "Pondering",
    "Mulling",
    "Brewing",
    "Simmering",
    "Marinating",
    "Percolating",
    "Stewing",
    "Conjuring",
    "Noodling",
    "Ruminating",
    "Musing",
    "Tinkering",
    "Whisking",
    "Plotting",
    "Hatching",
    "Crafting",
    "Forging",
    "Spinning",
    "Weaving",
    "Bewitching",
    "Synthesizing",
    "Concocting",
    "Distilling",
    "Channeling",
    "Divining",
    "Deciphering",
    "Untangling",
    "Unraveling",
    "Decoding",
    "Deliberating",
    "Contemplating",
    "Vibing",
    "Buzzing",
    "Dechiphering",
    "Actualizing ",
    "Scheming ",
    "Wrangling ",
    "Brewing ",
    "Manifesting ",
    "Moseying",
    "Prontificating ",
    "Forming ",
    "Whirring ",
    "Discombobulating ",
    "Boggling",
    "Befuddling",
    "Wibbling",
    "Frobbing",
    "Twiddling",
    "Doodling",
    "Squiggling",
    "Whirring",
    "Burbling",
    "Mumbling",
    "Flummoxing",
    "Fiddling",
    "Schmoozing",
    "Glimmering",
    "Sparkling",
    "Whomping",
    "Bonking",
    "Wobbling",
    "Snazzifying",
    "Jazzing",
    "Frazzling",
    "Sizzling",
    "Roasting",
    "Wrangling",
    "Finagling",
    "Bamboozling",
    "Hocus-pocusing",
    "Zhuzhing",
    "Tinkering ",
    "Pondering",
    "Pontificating",
    "Meandering",
    "Transmuting",
];

function pickThinkingWord(): string
{
    return THINKING_WORDS[Math.floor(Math.random() * THINKING_WORDS.length)];
}

type AgentPlanCompiled = {
    successful: boolean;
    errors: any[];
};

type AgentPlanToolsRequested = {
    successful: boolean;
};

type AgentStepProcessed = {
    successful: boolean;
    target: { type: string; ids: string[] } | null;
    description: string;
};

type AgentStepResult = {
    successful: boolean;
    skipped: boolean;
    result: any;
    description: string;
};

type AgentTaskRequested = {
    command: string;
    successful: boolean;
};

type AgentTaskResult = {
    successful: boolean;
    total_size: number;
    excerpt_size: number;
    excerpt: string;
};


type AgentTrackData = {
    classification: AgentTrackResponse_ClassificationRow | null;
    observation: AgentTrackResponse_ObservationRow[] | null;
    tools: AgentTrackResponse_ToolRow[] | null;
    plans: AgentTrackResponse_PlanRow[];
    plans_compiled: Record<string, AgentPlanCompiled>;
    plans_tools_requested: Record<string, AgentPlanToolsRequested>;
    steps: AgentTrackResponse_StepRow[];
    step_processed: Record<string, AgentStepProcessed>;
    step_result: Record<string, AgentStepResult>;
    tasks: AgentTrackResponse_TaskRow[];
    task_requested: Record<string, AgentTaskRequested>;
    task_result: Record<string, AgentTaskResult>;
    analyses: AgentTrackResponse_AnalyzerRow[];
    analyses_tools_retrieved: Record<string, { successful: boolean }>;
    report_plan: AgentTrackResponse_ReportPlanRow | null;
    error: AgentTrackResponse_ErrorRow | null;
};

interface RunAgentProps extends NSARouterMakerResult, ExpertRouterProps
{
    run: RunRow;
    defer?: boolean;
    onRunChanged?: (run: RunRow) => void;
    onChatChanged?: (chat: ChatRow) => void;
}

interface RunAgentState
{
    run: RunRow;
    track: AgentTrackData | null;
    expanded: Record<string, boolean>;
    jsonOpen: Record<string, boolean>;
    thinkingWord: string;
    thinkingDots: number;
    deferred: boolean;
}

class RunAgent extends Component<RunAgentProps, RunAgentState>
{
    private fetching = false;
    private unmounted = false;
    private finished = false;
    private trackHash: string | null = null;
    private chatHash: string | null = null;
    private jsonFormatter = new JsonFormatter("100%");
    private namirasoftMap: NamirasoftMap;
    private storage = new IStorageLocal();
    private wordTimer: ReturnType<typeof setInterval> | null = null;
    private dotsTimer: ReturnType<typeof setInterval> | null = null;
    private pollTimer: ReturnType<typeof setInterval> | null = null;

    constructor(props: RunAgentProps)
    {
        super(props);
        this.state = {
            run: props.run,
            track: null,
            expanded: {},
            jsonOpen: {},
            thinkingWord: pickThinkingWord(),
            thinkingDots: 1,
            deferred: props.defer ?? false,
        };
        this.namirasoftMap = new NamirasoftMap(props.account.token_manager, console.error);
        this.handleLoadDetails = this.handleLoadDetails.bind(this);
    }

    private renderId(id: string | null | undefined, fallback: ReactNode = "—"): ReactNode
    {
        if (!id)
            return fallback;
        return <NSID id={id} map={this.namirasoftMap} storage={this.storage} />;
    }

    override componentDidMount(): void
    {
        if (!this.state.deferred)
            this.startTracking();
    }

    private startTracking(): void
    {
        this.reload();
        this.pollTimer = setInterval(() =>
        {
            if (this.unmounted || RunHelper.isTerminalStatus(this.state.run.status))
            {
                clearInterval(this.pollTimer!);
                this.pollTimer = null;
                return;
            }
            this.reload();
        }, 1000);
        this.wordTimer = setInterval(() =>
        {
            if (!this.unmounted)
                this.setState({ thinkingWord: pickThinkingWord() });
        }, 3000);
        this.dotsTimer = setInterval(() =>
        {
            if (!this.unmounted)
                this.setState(prev => ({ thinkingDots: (prev.thinkingDots % 3) + 1 }));
        }, 500);
    }

    private handleLoadDetails(): void
    {
        this.setState({ deferred: false }, () => this.startTracking());
    }

    override componentWillUnmount(): void
    {
        this.unmounted = true;
        if (this.pollTimer)
        {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
        if (this.wordTimer)
        {
            clearInterval(this.wordTimer);
            this.wordTimer = null;
        }
        if (this.dotsTimer)
        {
            clearInterval(this.dotsTimer);
            this.dotsTimer = null;
        }
    }

    private async reload(): Promise<void>
    {
        if (this.fetching)
            return;
        if (RunHelper.isTerminalStatus(this.state.run.status))
            if (this.state.track != null)
            {
                if (this.finished)
                    return;
                this.finished = true;
            }

        this.fetching = true;
        try
        {
            let response = await this.props.servers.expert.agent.Track(this.state.run.id);
            if (!this.unmounted)
            {
                let chat = response?.input?.chat;
                if (chat)
                {
                    let newChatHash = JSON.stringify(chat);
                    if (newChatHash !== this.chatHash)
                    {
                        this.chatHash = newChatHash;
                        this.props.onChatChanged?.(chat);
                    }
                }
                let newHash = JSON.stringify(response);
                if (newHash !== this.trackHash)
                {
                    this.trackHash = newHash;
                    this.setState({ track: response as unknown as AgentTrackData });
                }
            }
        }
        catch { }
        finally
        {
            this.fetching = false;
        }
    }

    private toggle(key: string): void
    {
        this.setState(prev => ({
            ...prev,
            expanded: { ...prev.expanded, [key]: !prev.expanded[key] }
        }));
    }

    private isOpen(key: string): boolean
    {
        return !!this.state.expanded[key];
    }

    private toggleJson(key: string, e: MouseEvent): void
    {
        e.stopPropagation();
        this.setState(prev => ({
            ...prev,
            jsonOpen: { ...prev.jsonOpen, [key]: !prev.jsonOpen[key] }
        }));
    }

    private isJsonOpen(key: string): boolean
    {
        return !!this.state.jsonOpen[key];
    }

    private renderJsonButton(key: string): ReactNode
    {
        let active = this.isJsonOpen(key);
        let cls = `${Styles.ns_agent_json_button} ${active ? Styles.ns_agent_json_button_active : ""}`;
        return (
            <button
                type="button"
                className={cls}
                onClick={(e) => this.toggleJson(key, e)}
                title={active ? "Hide JSON" : "Show JSON"}
            >
                {"{ }"}
            </button>
        );
    }

    private renderJsonPanel(key: string, value: any): ReactNode
    {
        if (!this.isJsonOpen(key))
            return null;
        return (
            <div className={Styles.ns_agent_json_panel}>
                {this.jsonFormatter.format(value, null!, null!, false)}
            </div>
        );
    }

    private renderSeverity(severity: number): ReactNode
    {
        let cls =
            severity >= 7 ? Styles.ns_agent_severity_high :
                severity >= 4 ? Styles.ns_agent_severity_medium :
                    Styles.ns_agent_severity_low;
        return <span className={`${Styles.ns_agent_severity} ${cls}`}>{severity}</span>;
    }

    private renderCaret(open: boolean): ReactNode
    {
        return (
            <svg
                className={`${Styles.ns_agent_section_caret} ${open ? Styles.ns_agent_section_caret_open : ""}`}
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <polyline points="6 4 10 8 6 12" />
            </svg>
        );
    }

    private renderSection(
        key: string,
        title: string,
        count: number | null,
        summary: ReactNode,
        body: ReactNode,
        jsonValue?: any,
    ): ReactNode
    {
        let open = this.isOpen(key);
        let hasJson = jsonValue !== undefined;
        let jsonKey = `${key}_json`;
        return (
            <div className={`${Styles.ns_agent_section} ${!open ? Styles.ns_agent_collapsed : ""}`} key={key}>
                <div className={Styles.ns_agent_section_header} onClick={() => this.toggle(key)}>
                    {this.renderCaret(open)}
                    <span className={Styles.ns_agent_section_title}>{title}</span>
                    {count !== null && <span className={Styles.ns_agent_section_count}>{count}</span>}
                    {(summary || hasJson) && (
                        <span className={Styles.ns_agent_section_right}>
                            {summary && <span className={Styles.ns_agent_section_summary}>{summary}</span>}
                            {hasJson && this.renderJsonButton(jsonKey)}
                        </span>
                    )}
                </div>
                {open && (
                    <div className={Styles.ns_agent_section_body}>
                        {hasJson && this.renderJsonPanel(jsonKey, jsonValue)}
                        {body}
                    </div>
                )}
            </div>
        );
    }

    private renderClassification(c: AgentTrackResponse_ClassificationRow | null): ReactNode
    {
        if (!c)
            return null;

        let body = (
            <div className={Styles.ns_agent_field_grid}>
                <div className={Styles.ns_agent_field}>
                    <span className={Styles.ns_agent_field_label}>Category</span>
                    <span className={Styles.ns_agent_field_value}>{c.category || "—"}</span>
                </div>
                <div className={Styles.ns_agent_field}>
                    <span className={Styles.ns_agent_field_label}>Intent</span>
                    <span className={Styles.ns_agent_field_value}>{c.intent || "—"}</span>
                </div>
                <div className={Styles.ns_agent_field}>
                    <span className={Styles.ns_agent_field_label}>Priority</span>
                    <span className={Styles.ns_agent_field_value}>{c.priority || "—"}</span>
                </div>
                <div className={Styles.ns_agent_field}>
                    <span className={Styles.ns_agent_field_label}>Confidence</span>
                    <span className={Styles.ns_agent_field_value}>
                        {typeof c.confidence === "number" ? `${c.confidence.toFixed(1)}%` : "—"}
                    </span>
                </div>
                <div className={Styles.ns_agent_field} style={{ gridColumn: "1 / -1" }}>
                    <span className={Styles.ns_agent_field_label}>Domains</span>
                    <div className={Styles.ns_agent_chips}>
                        {(c.domains ?? []).length === 0
                            ? <span className={Styles.ns_agent_field_value}>—</span>
                            : c.domains.map((d, i) => <span key={i} className={Styles.ns_agent_chip}>{d}</span>)
                        }
                    </div>
                </div>
            </div>
        );
        return this.renderSection("classification", "Classification", null, null, body, c);
    }

    private renderObservations(observations: AgentTrackResponse_ObservationRow[] | null): ReactNode
    {
        let list = observations ?? [];
        if (list.length === 0)
            return null;
        let body = (
            <div className={Styles.ns_agent_table_wrapper}>
                <table className={Styles.ns_agent_table}>
                    <thead>
                        <tr>
                            <th className={Styles.ns_agent_table_col_name}>Entity Type</th>
                            <th className={Styles.ns_agent_table_col_name}>Entity ID</th>
                            <th>Observation</th>
                            <th className={Styles.ns_agent_table_col_index}>Severity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((o, i) => (
                            <tr key={i}>
                                <td>{o.entity_type}</td>
                                <td>{this.renderId(o.entity_id)}</td>
                                <td>{o.observation}</td>
                                <td>{this.renderSeverity(o.severity)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
        return this.renderSection("observations", "Observations", list.length, null, body, list);
    }

    private namesByRequestedBy(items: AgentTrackResponse_ToolRow[] | null, type: AWorkflowStateType_ResourceRequestedByType, rid: string): string[]
    {
        if (!items)
            return [];
        return items.filter(t => t.requested_by_type === type && (type === "init" || t.requested_by_rid === rid)).map(t => t.name);
    }

    private renderChipsSection(key: string, title: string, items: string[] | null): ReactNode
    {
        let list = items ?? [];
        if (list.length === 0)
            return null;
        let body = (
            <div className={Styles.ns_agent_chips}>
                {list.map((item, i) => (
                    <span key={i} className={`${Styles.ns_agent_chip} ${Styles.ns_agent_chip_neutral}`}>{item}</span>
                ))}
            </div>
        );
        return this.renderSection(key, title, list.length, null, body, list);
    }

    private renderPlansList(track: AgentTrackData): ReactNode
    {
        let plans = track.plans ?? [];
        if (plans.length === 0)
            return null;

        return (
            <>
                {
                    plans.map(plan =>
                    {
                        let analyses = (track.analyses ?? []).filter(a => a.plan_rid === plan.rid);
                        return (
                            <div className={Styles.ns_agent_plan_group} key={plan.rid}>
                                {this.renderPlan(track, plan)}
                                {this.renderMoreResources(plan, track)}
                                {this.renderAnalyses(plan.rid, analyses, track)}
                                {this.renderSelectedTools(plan.rid, analyses, track)}
                            </div>
                        );
                    })}
            </>
        );
    }

    private formatDecision(decision: string): string
    {
        return NamingConvention.auto.convert(decision, NamingConvention.First_word_case_space);
    }

    private renderDecisionBadge(decision: string): ReactNode
    {
        return <StatusBadge status="successful" label={this.formatDecision(decision)} />;
    }

    private renderPlan(track: AgentTrackData, plan: AgentTrackResponse_PlanRow): ReactNode
    {
        let key = `plan_${plan.rid}`;
        let jsonKey = `${key}_json`;
        let open = this.isOpen(key);
        let compiled = track.plans_compiled?.[plan.rid];
        let output: any = plan.output ?? {};
        let planSteps: any[] = Array.isArray(output.steps) ? output.steps : [];
        let stepIds = new Set(planSteps.map((s: any) => s?.id).filter((x: any) => !!x));

        let stepRows = (track.steps ?? []).filter(s => stepIds.has(s.output?.id));
        let stepRids = new Set(stepRows.map(s => s.rid));
        let taskRows = (track.tasks ?? []).filter(t => stepRids.has(t.step_rid));

        return (
            <div className={`${Styles.ns_agent_plan} ${!open ? Styles.ns_agent_collapsed : ""}`} key={plan.rid}>
                <div className={Styles.ns_agent_plan_header} onClick={() => this.toggle(key)}>
                    {this.renderCaret(open)}
                    <span className={Styles.ns_agent_plan_title}>
                        Plan · iteration {plan.iteration}{plan.retry > 0 ? ` · retry ${plan.retry}` : ""}
                    </span>
                    <span className={Styles.ns_agent_plan_meta}>
                        {output.decision && (
                            <span className={Styles.ns_agent_section_summary_item}>
                                Decision: {this.renderDecisionBadge(output.decision)}
                            </span>
                        )}
                        <span className={Styles.ns_agent_section_summary_item}>Steps: {stepRows.length}</span>
                        <span className={Styles.ns_agent_section_summary_item}>Tasks: {taskRows.length}</span>
                    </span>
                    {compiled
                        ? <StatusBadge status={compiled.successful ? "successful" : "failed"} label={compiled.successful ? "Compiled" : "Compile Failed"} />
                        : <StatusBadge status="pending" label="Compiling" />
                    }
                    {this.renderJsonButton(jsonKey)}
                </div>
                {open && (
                    <div className={Styles.ns_agent_plan_body}>
                        {this.renderJsonPanel(jsonKey, plan)}
                        {this.renderPlanMeta(plan, output)}
                        {this.renderQuestions(output)}
                        {this.renderCompilation(plan.rid, compiled)}
                        {this.renderStepsSection(plan.rid, stepRows, track)}
                    </div>
                )}
            </div>
        );
    }

    private renderPlanMeta(plan: AgentTrackResponse_PlanRow, output: any): ReactNode
    {
        let jsonKey = `plan_${plan.rid}_meta_json`;
        return (
            <div className={Styles.ns_agent_subsection}>
                <div className={Styles.ns_agent_subsection_header}>
                    <span className={Styles.ns_agent_subsection_title}>Plan Details</span>
                    <span className={Styles.ns_agent_subsection_summary}>
                        {this.renderJsonButton(jsonKey)}
                    </span>
                </div>
                <div className={Styles.ns_agent_subsection_body}>
                    {this.renderJsonPanel(jsonKey, output)}
                    <div className={Styles.ns_agent_field_grid}>
                        <div className={Styles.ns_agent_field}>
                            <span className={Styles.ns_agent_field_label}>Plan ID</span>
                            <span className={Styles.ns_agent_field_value}>{output.id || plan.rid}</span>
                        </div>
                        <div className={Styles.ns_agent_field}>
                            <span className={Styles.ns_agent_field_label}>Decision</span>
                            <span className={Styles.ns_agent_field_value}>
                                {output.decision ? this.renderDecisionBadge(output.decision) : "—"}
                            </span>
                        </div>
                        <div className={Styles.ns_agent_field}>
                            <span className={Styles.ns_agent_field_label}>Iteration</span>
                            <span className={Styles.ns_agent_field_value}>{plan.iteration}</span>
                        </div>
                        <div className={Styles.ns_agent_field}>
                            <span className={Styles.ns_agent_field_label}>Retry</span>
                            <span className={Styles.ns_agent_field_value}>{plan.retry}</span>
                        </div>
                        {output.goal && (
                            <div className={Styles.ns_agent_field} style={{ gridColumn: "1 / -1" }}>
                                <span className={Styles.ns_agent_field_label}>Goal</span>
                                <span className={Styles.ns_agent_field_value}>{output.goal}</span>
                            </div>
                        )}
                        {output.reason && (
                            <div className={Styles.ns_agent_field} style={{ gridColumn: "1 / -1" }}>
                                <span className={Styles.ns_agent_field_label}>Reason</span>
                                <span className={Styles.ns_agent_field_value}>{output.reason}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private renderQuestions(output: any): ReactNode
    {
        let raw = output?.question;
        let questions: string[] = Array.isArray(raw)
            ? raw.filter((q: any) => typeof q === "string" && q.trim().length > 0)
            : (typeof raw === "string" && raw.trim().length > 0 ? [raw] : []);
        if (questions.length === 0)
            return null;

        let jsonKey = `questions_${(output?.id ?? "")}_json`;
        return (
            <div className={Styles.ns_agent_subsection}>
                <div className={Styles.ns_agent_subsection_header}>
                    <span className={Styles.ns_agent_subsection_title}>Questions</span>
                    <span className={Styles.ns_agent_section_count}>{questions.length}</span>
                    <span className={Styles.ns_agent_subsection_summary}>
                        Clarification requested from user
                        {this.renderJsonButton(jsonKey)}
                    </span>
                </div>
                <div className={Styles.ns_agent_subsection_body}>
                    {this.renderJsonPanel(jsonKey, questions)}
                    <ol className={Styles.ns_agent_question_list}>
                        {questions.map((q, i) => (
                            <li key={i} className={Styles.ns_agent_question_item}>
                                <NSMarkdown text={q} />
                            </li>
                        ))}
                    </ol>
                </div>
            </div>
        );
    }

    private renderMoreResources(plan: AgentTrackResponse_PlanRow, track: AgentTrackData): ReactNode
    {
        let output: any = plan.output ?? {};
        if (output.decision !== "more_tools")
            return null;

        let requested = track.plans_tools_requested?.[plan.rid];
        let hint = typeof output.tool_hint === "string" ? output.tool_hint : "";
        let pickedTools = this.namesByRequestedBy(track.tools, AWorkflowStateType_ResourceRequestedByType.plan, plan.rid);

        let status: StatusKind;
        let label: string;
        if (!requested)
        {
            status = "pending";
            label = "Picking tools";
        }
        else if (!requested.successful)
        {
            status = "skipped";
            label = "No new tools available";
        }
        else
        {
            status = "successful";
            label = `Picked ${pickedTools.length}`;
        }

        let key = `more_tools_${plan.rid}`;
        let jsonKey = `${key}_json`;
        let open = this.isOpen(key);

        return (
            <div className={`${Styles.ns_agent_subsection} ${!open ? Styles.ns_agent_collapsed : ""}`} key={key}>
                <div className={Styles.ns_agent_subsection_header} onClick={() => this.toggle(key)} style={{ cursor: "pointer", userSelect: "none" }}>
                    {this.renderCaret(open)}
                    <span className={Styles.ns_agent_subsection_title}>More Tools</span>
                    <span className={Styles.ns_agent_subsection_summary}>
                        <StatusBadge status={status} label={label} />
                        {this.renderJsonButton(jsonKey)}
                    </span>
                </div>
                {open && (
                    <div className={Styles.ns_agent_subsection_body}>
                        {this.renderJsonPanel(jsonKey, { tool_hint: hint, requested })}
                        <div className={Styles.ns_agent_field_grid}>
                            <div className={Styles.ns_agent_field} style={{ gridColumn: "1 / -1" }}>
                                <span className={Styles.ns_agent_field_label}>Tool Hint</span>
                                <span className={Styles.ns_agent_field_value}>{hint || "—"}</span>
                            </div>
                            {requested && pickedTools.length > 0 && (
                                <div className={Styles.ns_agent_field} style={{ gridColumn: "1 / -1" }}>
                                    <span className={Styles.ns_agent_field_label}>Picked Tools ({pickedTools.length})</span>
                                    <div className={Styles.ns_agent_chips}>
                                        {pickedTools.map((name, i) => (
                                            <span key={i} className={`${Styles.ns_agent_chip} ${Styles.ns_agent_chip_neutral}`}>{name}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {requested && pickedTools.length === 0 && (
                                <div className={Styles.ns_agent_field} style={{ gridColumn: "1 / -1" }}>
                                    <span className={Styles.ns_agent_field_value}>No additional tools were picked.</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private formatValue(v: any): string
    {
        if (v === null || v === undefined)
            return "—";
        if (typeof v !== "object")
            return String(v);
        if (v.type === "const" || v.const !== undefined)
            return JSON.stringify(v.const ?? v);
        if (v.type === "column_ref" || v.column_ref !== undefined)
            return `.${v.column_ref}`;
        if (v.type === "step_ref" || v.step_ref !== undefined)
        {
            let ref = v.step_ref ?? {};
            return `@${ref.step ?? "?"}${ref.path ? `.${ref.path}` : ""}`;
        }
        try { return JSON.stringify(v); }
        catch { return String(v); }
    }

    private formatCondition(c: any): string
    {
        if (!c) return "—";
        return `${this.formatValue(c.left)} ${c.operation ?? "?"} ${this.formatValue(c.right)}`;
    }

    private renderCompilation(planRid: string, compiled: AgentPlanCompiled | undefined): ReactNode
    {
        let jsonKey = `plan_${planRid}_compiled_json`;
        return (
            <div className={Styles.ns_agent_subsection}>
                <div className={Styles.ns_agent_subsection_header}>
                    <span className={Styles.ns_agent_subsection_title}>Compilation</span>
                    <span className={Styles.ns_agent_subsection_summary}>
                        {compiled
                            ? <StatusBadge status={compiled.successful ? "successful" : "failed"} label={compiled.successful ? "Compiled" : "Failed"} />
                            : <StatusBadge status="pending" label="Pending" />
                        }
                        {compiled && this.renderJsonButton(jsonKey)}
                    </span>
                </div>
                <div className={Styles.ns_agent_subsection_body}>
                    {compiled && this.renderJsonPanel(jsonKey, compiled)}
                    {!compiled && <div className={Styles.ns_agent_section_empty}>Awaiting compilation.</div>}
                    {compiled && compiled.successful && <div className={Styles.ns_agent_field_value}>Plan compiled successfully.</div>}
                    {compiled && !compiled.successful && (
                        <div className={Styles.ns_agent_compile_errors}>
                            {(compiled.errors ?? []).length === 0
                                ? <div className={Styles.ns_agent_compile_error}>Compilation failed without a specific error.</div>
                                : compiled.errors.map((err, i) => (
                                    <div key={i} className={Styles.ns_agent_compile_error}>
                                        {typeof err === "string" ? err : (err?.message ?? JSON.stringify(err))}
                                        {err?.path && <div style={{ opacity: 0.7, marginTop: "0.25rem" }}>at {String(err.path)}</div>}
                                    </div>
                                ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private renderStepsSection(planRid: string, steps: AgentTrackResponse_StepRow[], track: AgentTrackData): ReactNode
    {
        if (steps.length === 0)
            return null;

        let ok = 0, fail = 0, skip = 0;
        for (let s of steps)
        {
            let p = track.step_processed?.[s.rid];
            let r = track.step_result?.[s.rid];
            if (r?.skipped) skip++;
            else if (r) (r.successful ? ok++ : fail++);
            else if (p && !p.successful) fail++;
        }

        let jsonKey = `plan_${planRid}_steps_json`;
        let jsonValue = steps.map(s => ({
            step: s,
            processed: track.step_processed?.[s.rid] ?? null,
            result: track.step_result?.[s.rid] ?? null,
        }));

        return (
            <div className={Styles.ns_agent_subsection}>
                <div className={Styles.ns_agent_subsection_header}>
                    <span className={Styles.ns_agent_subsection_title}>Steps</span>
                    <span className={Styles.ns_agent_section_count}>{steps.length}</span>
                    <span className={Styles.ns_agent_subsection_summary}>
                        <StatusCount count={ok} label="Successful" status="successful" />
                        <StatusCount count={fail} label="Failed" status="failed" />
                        <StatusCount count={skip} label="Skipped" status="skipped" />
                        {this.renderJsonButton(jsonKey)}
                    </span>
                </div>
                <div className={Styles.ns_agent_subsection_body}>
                    {this.renderJsonPanel(jsonKey, jsonValue)}
                    <div className={Styles.ns_agent_step_list}>
                        {steps.map(s => this.renderStepCard(s, track))}
                    </div>
                </div>
            </div>
        );
    }

    private renderStepCard(step: AgentTrackResponse_StepRow, track: AgentTrackData): ReactNode
    {
        let key = `step_${step.rid}`;
        let jsonKey = `${key}_json`;
        let open = this.isOpen(key);
        let processed = track.step_processed?.[step.rid];
        let result = track.step_result?.[step.rid];
        let out: any = step.output ?? {};
        let task: any = out.task ?? {};
        let target: any = out.target ?? {};

        let skipped = !!result?.skipped;
        let status: StatusKind;
        let label: string | undefined;
        if (skipped)
        {
            status = "skipped";
            label = "Skipped";
        }
        else if (result)
        {
            status = result.successful ? "successful" : "failed";
        }
        else if (processed && !processed.successful)
        {
            status = "failed";
        }
        else
        {
            status = "pending";
        }

        let stepTasks = (track.tasks ?? []).filter(t => t.step_rid === step.rid);
        let message = status === "failed" ? processed?.description : result?.description;
        let messageIsError = status === "failed";
        let hasTarget = target && Object.keys(target).length > 0;

        return (
            <div className={`${Styles.ns_agent_step_card} ${!open ? Styles.ns_agent_collapsed : ""}`} key={step.rid}>
                <div className={Styles.ns_agent_step_header} onClick={() => this.toggle(key)}>
                    {this.renderCaret(open)}
                    <span className={Styles.ns_agent_step_title}>{out.id ?? step.rid}</span>
                    {task.name && <span className={Styles.ns_agent_step_meta}>· {task.name}</span>}
                    <span className={Styles.ns_agent_step_right}>
                        {hasTarget && target.domain && (
                            <span className={`${Styles.ns_agent_chip} ${Styles.ns_agent_chip_neutral}`}>{target.domain}</span>
                        )}
                        <span className={Styles.ns_agent_section_summary_item}>Tasks: {stepTasks.length}</span>
                        <StatusBadge status={status} label={label} />
                        {this.renderJsonButton(jsonKey)}
                    </span>
                </div>
                {open && (
                    <div className={Styles.ns_agent_step_body}>
                        {this.renderJsonPanel(jsonKey, { step, processed, result })}
                        {(out.goal || task.type || message) && (
                            <div className={Styles.ns_agent_field_grid}>
                                {out.goal && (
                                    <div className={Styles.ns_agent_field} style={{ gridColumn: "1 / -1" }}>
                                        <span className={Styles.ns_agent_field_label}>Goal</span>
                                        <span className={Styles.ns_agent_field_value}>{out.goal}</span>
                                    </div>
                                )}
                                {task.type && (
                                    <div className={Styles.ns_agent_field}>
                                        <span className={Styles.ns_agent_field_label}>Task Type</span>
                                        <span className={Styles.ns_agent_field_value}>{task.type}</span>
                                    </div>
                                )}
                                {message && (
                                    <div className={Styles.ns_agent_field} style={{ gridColumn: "1 / -1" }}>
                                        <span className={Styles.ns_agent_field_label}>Message</span>
                                        {messageIsError
                                            ? <div className={Styles.ns_agent_reason}>{message}</div>
                                            : <span className={Styles.ns_agent_field_value}>{message}</span>
                                        }
                                    </div>
                                )}
                            </div>
                        )}
                        {hasTarget && this.renderStepTarget(step.rid, target)}
                        {this.renderStepTasks(step.rid, stepTasks, track)}
                    </div>
                )}
            </div>
        );
    }

    private renderStepTarget(stepRid: string, target: any): ReactNode
    {
        let query = target.query ?? {};
        let where = query.where ?? {};
        let allConds: any[] = Array.isArray(where.all) ? where.all : [];
        let anyConds: any[] = Array.isArray(where.any) ? where.any : [];
        let hasQuery = allConds.length > 0 || anyConds.length > 0 || query.sort || query.limit != null;
        let jsonKey = `step_${stepRid}_target_json`;

        return (
            <div className={Styles.ns_agent_subsection}>
                <div className={Styles.ns_agent_subsection_header}>
                    <span className={Styles.ns_agent_subsection_title}>Target</span>
                    <span className={Styles.ns_agent_subsection_summary}>
                        {target.execution_mode && <span className={Styles.ns_agent_section_summary_item}>{target.execution_mode}</span>}
                        {this.renderJsonButton(jsonKey)}
                    </span>
                </div>
                <div className={Styles.ns_agent_subsection_body}>
                    {this.renderJsonPanel(jsonKey, target)}
                    <div className={Styles.ns_agent_field_grid}>
                        <div className={Styles.ns_agent_field}>
                            <span className={Styles.ns_agent_field_label}>Domain</span>
                            <span className={Styles.ns_agent_field_value}>{target.domain ?? "—"}</span>
                        </div>
                        <div className={Styles.ns_agent_field}>
                            <span className={Styles.ns_agent_field_label}>Execution Mode</span>
                            <span className={Styles.ns_agent_field_value}>{target.execution_mode ?? "—"}</span>
                        </div>
                        {target.from_step && (
                            <div className={Styles.ns_agent_field}>
                                <span className={Styles.ns_agent_field_label}>From Step</span>
                                <span className={Styles.ns_agent_field_value}>{target.from_step}</span>
                            </div>
                        )}
                    </div>
                    {hasQuery && (
                        <div className={Styles.ns_agent_query_block}>
                            {allConds.length > 0 && (
                                <div>
                                    <span className={Styles.ns_agent_field_label}>where (all)</span>
                                    <ul className={Styles.ns_agent_query_list}>
                                        {allConds.map((c, j) => (
                                            <li key={`a${j}`}>
                                                <span className={Styles.ns_agent_code}>{this.formatCondition(c)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {anyConds.length > 0 && (
                                <div>
                                    <span className={Styles.ns_agent_field_label}>where (any)</span>
                                    <ul className={Styles.ns_agent_query_list}>
                                        {anyConds.map((c, j) => (
                                            <li key={`o${j}`}>
                                                <span className={Styles.ns_agent_code}>{this.formatCondition(c)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {query.sort && (
                                <div className={Styles.ns_agent_description}>
                                    sort: {query.sort.column} {query.sort.direction}
                                </div>
                            )}
                            {query.limit != null && (
                                <div className={Styles.ns_agent_description}>limit: {query.limit}</div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private renderStepTasks(stepRid: string, tasks: AgentTrackResponse_TaskRow[], track: AgentTrackData): ReactNode
    {
        if (tasks.length === 0)
            return null;

        return (
            <Task
                sectionKey={`step_${stepRid}_tasks`}
                tasks={tasks}
                taskRequested={track.task_requested ?? {}}
                taskResult={track.task_result ?? {}}
                namirasoftMap={this.namirasoftMap}
                storage={this.storage}
                jsonFormatter={this.jsonFormatter}
                showName={false}
                initiallyOpen={false}
            />
        );
    }

    private renderAnalyses(planRid: string, analyses: AgentTrackResponse_AnalyzerRow[], track: AgentTrackData): ReactNode
    {
        if (analyses.length === 0)
            return null;

        let key = `analyzer_${planRid}`;
        let jsonKey = `plan_${planRid}_analyses_json`;
        let open = this.isOpen(key);
        let jsonValue = analyses.map(a => ({
            analyzer: a,
            tools_retrieved: track.analyses_tools_retrieved?.[a.rid] ?? null,
        }));
        let latestDecision = analyses[analyses.length - 1]?.output?.decision;

        return (
            <div className={`${Styles.ns_agent_subsection} ${!open ? Styles.ns_agent_collapsed : ""}`}>
                <div className={Styles.ns_agent_subsection_header} onClick={() => this.toggle(key)} style={{ cursor: "pointer", userSelect: "none" }}>
                    {this.renderCaret(open)}
                    <span className={Styles.ns_agent_subsection_title}>Analyzer</span>
                    <span className={Styles.ns_agent_section_count}>{analyses.length}</span>
                    <span className={Styles.ns_agent_subsection_summary}>
                        {
                            latestDecision && (
                                latestDecision === "sufficient"
                                    ? <StatusBadge status="successful" label="Sufficient" />
                                    : <StatusBadge status="pending" label="Need More Plan" />
                            )}
                        {this.renderJsonButton(jsonKey)}
                    </span>
                </div>
                {open && (
                    <div className={Styles.ns_agent_subsection_body}>
                        {this.renderJsonPanel(jsonKey, jsonValue)}
                        <div className={Styles.ns_agent_field_grid} style={{ gridTemplateColumns: "1fr" }}>
                            {analyses.map(a =>
                            {
                                let toolsRetrieved = track.analyses_tools_retrieved?.[a.rid];
                                let toolsList = this.namesByRequestedBy(track.tools, AWorkflowStateType_ResourceRequestedByType.analysis, a.rid);
                                return (
                                    <div key={a.rid} className={Styles.ns_agent_field_grid}>
                                        <div className={Styles.ns_agent_field}>
                                            <span className={Styles.ns_agent_field_label}>Decision</span>
                                            <span className={Styles.ns_agent_field_value}>
                                                {a.output?.decision === "sufficient"
                                                    ? <StatusBadge status="successful" label="Sufficient" />
                                                    : <StatusBadge status="pending" label="Need More Plan" />
                                                }
                                            </span>
                                        </div>
                                        {a.output?.decision !== "sufficient" && (
                                            <div className={Styles.ns_agent_field}>
                                                <span className={Styles.ns_agent_field_label}>Tools Retrieved</span>
                                                <span className={Styles.ns_agent_field_value}>
                                                    {toolsRetrieved
                                                        ? <StatusBadge status={toolsRetrieved.successful ? "successful" : "failed"} />
                                                        : <StatusBadge status="pending" />
                                                    }
                                                </span>
                                            </div>
                                        )}
                                        {a.output?.reasoning && (
                                            <div className={Styles.ns_agent_field} style={{ gridColumn: "1 / -1" }}>
                                                <span className={Styles.ns_agent_field_label}>Reasoning</span>
                                                <span className={Styles.ns_agent_field_value}>{a.output.reasoning}</span>
                                            </div>
                                        )}
                                        {a.output?.instruction && (
                                            <div className={Styles.ns_agent_field} style={{ gridColumn: "1 / -1" }}>
                                                <span className={Styles.ns_agent_field_label}>Instruction</span>
                                                <span className={Styles.ns_agent_field_value}>{a.output.instruction}</span>
                                            </div>
                                        )}
                                        {(a.output?.capabilities?.length ?? 0) > 0 && (
                                            <div className={Styles.ns_agent_field} style={{ gridColumn: "1 / -1" }}>
                                                <span className={Styles.ns_agent_field_label}>Capabilities</span>
                                                <div className={Styles.ns_agent_chips}>
                                                    {a.output.capabilities.map((cap, i) => (
                                                        <span key={i} className={`${Styles.ns_agent_chip} ${Styles.ns_agent_chip_neutral}`}>{cap}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {toolsList.length > 0 && (
                                            <div className={Styles.ns_agent_field} style={{ gridColumn: "1 / -1" }}>
                                                <span className={Styles.ns_agent_field_label}>Tools ({toolsList.length})</span>
                                                <div className={Styles.ns_agent_chips}>
                                                    {toolsList.map((tool, i) => (
                                                        <span key={i} className={`${Styles.ns_agent_chip} ${Styles.ns_agent_chip_neutral}`}>{tool}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private renderSelectedTools(planRid: string, analyses: AgentTrackResponse_AnalyzerRow[], track: AgentTrackData): ReactNode
    {
        if (analyses.length === 0)
            return null;
        let analysisRids = new Set(analyses.map(a => a.rid));
        let names = Array.from(new Set(
            (track.tools ?? [])
                .filter(t => t.requested_by_type === "analysis" && analysisRids.has(t.requested_by_rid))
                .map(t => t.name)
        ));
        if (names.length === 0)
            return null;

        let key = `selected_tools_${planRid}`;
        let jsonKey = `${key}_json`;
        let open = this.isOpen(key);

        return (
            <div className={`${Styles.ns_agent_subsection} ${!open ? Styles.ns_agent_collapsed : ""}`}>
                <div className={Styles.ns_agent_subsection_header} onClick={() => this.toggle(key)} style={{ cursor: "pointer", userSelect: "none" }}>
                    {this.renderCaret(open)}
                    <span className={Styles.ns_agent_subsection_title}>Selected Tools</span>
                    <span className={Styles.ns_agent_section_count}>{names.length}</span>
                    <span className={Styles.ns_agent_subsection_summary}>
                        {this.renderJsonButton(jsonKey)}
                    </span>
                </div>
                {open && (
                    <div className={Styles.ns_agent_subsection_body}>
                        {this.renderJsonPanel(jsonKey, names)}
                        <div className={Styles.ns_agent_chips}>
                            {names.map((name, i) => (
                                <span key={i} className={`${Styles.ns_agent_chip} ${Styles.ns_agent_chip_neutral}`}>{name}</span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private formatBytes(n: number): string
    {
        if (!Number.isFinite(n) || n < 0) return "—";
        if (n < 1024) return `${n} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
        if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
        return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    }

    private renderReportPlan(report: AgentTrackResponse_ReportPlanRow | null): ReactNode
    {
        if (!report)
            return null;
        let reason = (report as any).reason as string | undefined;
        let body = (
            <div className={Styles.ns_agent_field_grid}>
                <div className={Styles.ns_agent_field}>
                    <span className={Styles.ns_agent_field_label}>Strategy</span>
                    <span className={Styles.ns_agent_field_value}>
                        <StatusBadge status="successful" label={report.strategy} />
                    </span>
                </div>
                <div className={Styles.ns_agent_field}>
                    <span className={Styles.ns_agent_field_label}>Total Size</span>
                    <span className={Styles.ns_agent_field_value}>{this.formatBytes(report.sum_of_total_size)}</span>
                </div>
                <div className={Styles.ns_agent_field}>
                    <span className={Styles.ns_agent_field_label}>Excerpt Size</span>
                    <span className={Styles.ns_agent_field_value}>{this.formatBytes(report.sum_of_excerpt_size)}</span>
                </div>
                {
                    reason && (
                        <div className={Styles.ns_agent_field} style={{ gridColumn: "1 / -1" }}>
                            <span className={Styles.ns_agent_field_label}>Reason</span>
                            <span className={Styles.ns_agent_field_value}>{reason}</span>
                        </div>
                    )
                }
            </div>
        );
        let summary = <StatusBadge status="successful" label={report.strategy} />;
        return this.renderSection("report_plan", "Report Plan", null, summary, body, report);
    }

    private renderError(error: AgentTrackResponse_ErrorRow | null): ReactNode
    {
        if (!error)
            return null;
        return (
            <div className={Styles.ns_agent_error_box}>
                <span className={Styles.ns_agent_error_node}>[{error.node}]</span>
                {error.message}
            </div>
        );
    }

    private renderProgressVideo(): ReactNode
    {
        let status = this.state.run.status;
        if (RunHelper.isTerminalStatus(status))
            return null;
        let dots = ".".repeat(this.state.thinkingDots);
        return (
            <div className={Styles.ns_agent_progress}>
                <div className={Styles.ns_agent_thinking}>
                    <span className={Styles.ns_agent_thinking_word}>{this.state.thinkingWord.trim()}</span>
                    <span className={Styles.ns_agent_thinking_dots}>{dots}</span>
                </div>
                <div className={Styles.ns_agent_progress_video}>
                    <video
                        src="https://static.namirasoft.com/image/namirasoft/expert/concept/analyzer/96.webm"
                        autoPlay
                        loop
                        muted
                        playsInline
                    />
                </div>
            </div>
        );
    }

    override render()
    {
        let track = this.state.track;
        return (
            <RunLayout
                {...this.props}
                run={this.state.run}
                defer={this.state.deferred}
                onLoadDetails={this.handleLoadDetails}
                onRunChanged={run =>
                {
                    this.setState({ run }, () => this.reload());
                    this.props.onRunChanged?.(run);
                }}
            >
                {
                    !track && !this.state.deferred && (
                        <NSLoading
                            small={true}
                            hideTitle
                            description="Loading agent track…"
                        ></NSLoading>
                    )
                }
                {
                    track && (
                        <div className={Styles.ns_agent}>
                            {this.renderClassification(track.classification)}
                            {this.renderObservations(track.observation)}
                            {this.renderChipsSection("tools", "Tools", this.namesByRequestedBy(track.tools, AWorkflowStateType_ResourceRequestedByType.init, ""))}
                            {this.renderPlansList(track)}
                            {this.renderReportPlan(track.report_plan)}
                            {this.renderError(track.error)}
                        </div>
                    )
                }
                {this.renderProgressVideo()}
            </RunLayout>
        );
    }
}

export default RunAgent;